/**
 * Organizations Page with Server-Side Search (Beta)
 *
 * This version uses OpenSearch for fast pagination, sorting, and filtering.
 * Tree data (products) is disabled in this mode - organizations are flat.
 *
 * Toggle between this and OrganizationsPage via feature flag or route.
 */

import { Alert, Box, Button } from '@mui/material';
import {
  GridColDef,
  GridDensity,
  GridFilterModel,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useQueryClient } from '@tanstack/react-query';
import { useContactsItems } from 'api/aws/contacts/useContactItems';
import useUploadPhoto from 'api/aws/organizations/useUploadPhoto';
import { useDeleteItems } from 'api/aws/organizations_table/useDeleteItems';
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache';
import { useInlineMassUpdate } from 'api/aws/useInlineMassUpdate';
import { useSetOrgHeartbeatLink } from 'api/aws/heartbeat/useOrgHeartbeatLink';
import { useActiveUser } from 'api/slack/useActiveUser';
import { useUserItems } from 'api/aws/users/useUserItems';
import {
  checkSearchHealth,
  ORGANIZATIONS_SEARCH_KEY,
  useServerSideOrganizations,
} from 'api/search';
import { useEntitiesFromSearch } from 'api/search/useEntitiesFromSearch';
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid';
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar';
import { loadModel, saveModel } from 'components/DataGrid/operators/loadModels';
import SelectionFooter from 'components/DataGrid/SelectionFooter';
import OrganizationsPopover from 'components/Organizations/OrganizationsPopover';
import { isAdmin } from 'config/roles.config';
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrganizationTableItem, PARENT_TYPES, Task } from 'types';
import {
  defaultColumnOrder,
  defaultFilterModel,
  defaultSortModel,
  defaultVisibilityModel,
  EDITABLE_FIELDS,
  pinnedColumns,
} from './defaultModels';
import { orgDetailsRoute } from '../utils';
import { getOrgRowClassNames } from './getOrgRowClassNames';
import { useOrganizationGridColumns } from './useOrganizationGridColumns';

// Editable fields that can be mass-updated via inline editing
// Note: Includes both column field names (for isCellEditable) and data field names (for getChangedFields)

const CONTEXT = 'organizations';

const OrganizationsPageServerSide = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: contacts } = useContactsItems();
  const { data: users } = useUserItems();
  const { parents } = useEntitiesFromSearch();
  const { showSnackbar } = useSnackbarContext();
  const { activeUser } = useActiveUser();
  const callerId: string = activeUser?.user?.id ?? '';
  const isGroboticAdmin = isAdmin(callerId);
  const { mutateAsync: setHeartbeatLink } = useSetOrgHeartbeatLink();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedOrganization, setSelectedOrganization] =
    useState<OrganizationTableItem | null>(null);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      ids: new Set<GridRowId>(),
      type: 'include',
    });
  const apiRef = useGridApiRef();
  const { mutate: deleteItems } = useDeleteItems();
  const { mutate: uploadMutation } = useUploadPhoto();
  const { syncOrganizationFields, patchOrganizationFieldsInCache } = useSyncQueryCache();

  // Inline mass update hook - enables editing any cell to update all selected rows
  // Pass resolvers so grid updates include resolved display values (_resolvedTeam, etc.)
  const { processRowUpdate, isUpdating } =
    useInlineMassUpdate<OrganizationTableItem>({
      entityType: 'organization',
      context: 'organizations',
      rowSelectionModel,
      apiRef,
      editableFields: EDITABLE_FIELDS,
      onMassUpdateComplete: (count, field) => {
        showSnackbar(
          `Updated "${field}" for ${count} organizations`,
          'success'
        );
      },
      resolvers: {
        users: users ?? [],
        contacts: contacts ?? [],
        // organizations will be passed later via a memo, or we use searchData
      },
    });

  // Server-side search state
  const [searchAvailable, setSearchAvailable] = useState<boolean | null>(null);
  const [syncStatus, setSyncStatus] = useState<{
    documentCount?: number;
    lastSyncTime?: string;
    isInitialized?: boolean;
  }>({});
  // Check if search is available on mount and get sync status
  useEffect(() => {
    const checkHealth = async () => {
      const result = await checkSearchHealth();
      setSearchAvailable(result.available);
      // Extract sync status from health response
      setSyncStatus({
        documentCount: result.documentCount,
        lastSyncTime: result.lastSyncTime,
        isInitialized: result.isInitialized,
      });
      if (!result.available) {
        console.warn(
          '[OrganizationsPageServerSide] Search not available:',
          result.message
        );
      }
    };

    checkHealth();

    // Refresh sync status every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load saved models from localStorage (with defaults as fallback)
  const savedSortModel = useMemo(
    () => loadModel<GridSortModel>(CONTEXT, 'SortModel', defaultSortModel),
    []
  );
  const savedFilterModel = useMemo(
    () =>
      loadModel<GridFilterModel>(CONTEXT, 'FilterModel', defaultFilterModel),
    []
  );

  // Use server-side hook with saved models as initial values
  const {
    rows: serverRows,
    aggregations,
    totalRowCount,
    isLoading: isSearchLoading,
    error: searchError,
    sortModel,
    onSortModelChange: hookSortModelChange,
    filterModel,
    onFilterModelChange: hookFilterModelChange,
    serverSideProps,
    refresh,
  } = useServerSideOrganizations({
    enabled: searchAvailable === true,
    pageSize: 100,
    initialSortModel: savedSortModel,
    initialFilterModel: savedFilterModel,
  });
  const isLoading =
    searchAvailable === null ||
    searchAvailable === false ||
    isSearchLoading ||
    isUpdating;
  // Wrap change handlers to persist to localStorage
  const onSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      saveModel(CONTEXT, 'SortModel', newSortModel);
      hookSortModelChange(newSortModel);
    },
    [hookSortModelChange]
  );

  const onFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      saveModel(CONTEXT, 'FilterModel', newFilterModel);
      hookFilterModelChange(newFilterModel);
    },
    [hookFilterModelChange]
  );

  const onClearFilters = useCallback(() => {
    const emptyFilters = { items: [], quickFilterValues: [] };
    saveModel(CONTEXT, 'FilterModel', emptyFilters);
    hookFilterModelChange(emptyFilters);
  }, [hookFilterModelChange]);

  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('orgDensityModel') as GridDensity | null;
    return saved ?? 'standard';
  });

  // Process rows to add client-side computed fields
  const processedRows = useMemo(() => {
    if (!serverRows) return [];

    // Build lookup maps for fallback (when server doesn't have resolved data)
    const contactById = new Map(
      (contacts ?? []).map((c) => [c.contacts_uuid, c])
    );
    const userById = new Map((users ?? []).map((u) => [u.user_uuid, u]));
    // Also map by Slack ID since owner fields often store Slack IDs
    (users ?? []).forEach((u) => {
      if (u.id) userById.set(u.id, u);
    });

    return serverRows.map((org) => {
      // Use server-resolved entities when available, fallback to client-side lookup
      const primaryContact =
        org._resolvedPrimaryContact ||
        (org?.primaryContact?.[0]
          ? contactById.get(org.primaryContact[0])
          : undefined);

      const owner =
        org._resolvedOwner ||
        (org?.owner?.[0] ? userById.get(org.owner[0]) : undefined);

      const contractedBy = org._resolvedContractedBy?.length
        ? org._resolvedContractedBy
        : Array.isArray(org.contractedBy)
          ? org.contractedBy
          : [];

      const affiliated = org._resolvedAffiliated?.length
        ? org._resolvedAffiliated
        : Array.isArray(org.affiliated)
          ? org.affiliated
          : [];

      const team_details = org._resolvedTeamMembers?.length
        ? org._resolvedTeamMembers
        : (org?.team_details ?? [])
            .map((u) => userById.get(u.user_uuid))
            .filter(Boolean);

      // Build related contacts from IDs
      const contactIds = Array.isArray(org.contacts) ? org.contacts : [];
      const relatedContacts = contactIds
        .map((id) => contactById.get(id))
        .filter(Boolean);

      return {
        ...org,
        relatedContacts,
        primaryContact,
        owner,
        contractedBy,
        affiliated,
        team_details,
        rowId: org.org_uuid || org.PK,
        isParentOrg: PARENT_TYPES?.has(org?.type),
        isParent: true, // All rows are parent orgs in flat mode
        id: org.org_uuid || org.PK, // Ensure id is set
      };
    });
  }, [serverRows, contacts, users]);

  // Get column definitions with editable cells
  // Pass users, contacts, and organizations so edit cells have options for dropdowns
  const { organizationGridColumns } = useOrganizationGridColumns({
    users: users ?? [],
    contacts: contacts ?? [],
    organizations: processedRows,
    parentOrganizations: parents ?? [],
    canEditExternalIds: isGroboticAdmin,
  });

  // A Heartbeat id is never written as a bare field. The link route reads the
  // account from Heartbeat, refuses one that doesn't exist or is already
  // linked elsewhere, and writes its name and NetSuite id with it.
  const handleProcessRowUpdate = useCallback(
    async (
      newRow: OrganizationTableItem,
      oldRow: OrganizationTableItem
    ): Promise<OrganizationTableItem> => {
      const nextId = String(newRow?.heartbeat_account_id ?? '').trim();
      const prevId = String(oldRow?.heartbeat_account_id ?? '').trim();
      if (nextId === prevId) return processRowUpdate(newRow, oldRow);

      if (!isGroboticAdmin) {
        showSnackbar('Only Grobotic admins can change the Heartbeat link', 'error');
        return oldRow;
      }
      try {
        const result = await setHeartbeatLink({
          PK: newRow.PK,
          heartbeatAccountId: nextId,
          userId: callerId,
        });
        if (result.netsuiteConflict) {
          showSnackbar(
            `Linked. NetSuite ID left at ${result.netsuiteConflict.grobotic}; Heartbeat has ${result.netsuiteConflict.heartbeat}. Check which is right.`,
            'warning'
          );
        } else {
          showSnackbar(
            result.heartbeat_account_id
              ? `Linked to Heartbeat ${result.heartbeat_account_id}${result.heartbeat_account_name ? `, ${result.heartbeat_account_name}` : ''}`
              : 'Heartbeat link removed',
            'success'
          );
        }
        const linkedFields = {
          heartbeat_account_id: result.heartbeat_account_id ?? '',
          heartbeat_account_name: result.heartbeat_account_name ?? '',
          netsuite_id: result.netsuite_id ?? '',
        };
        // The route re-indexes the org server side; patch the app-wide cache
        // and refetch the grid's pages so neither brings the old link back.
        patchOrganizationFieldsInCache(
          queryClient,
          newRow.PK,
          newRow.SK,
          Object.entries(linkedFields).map(([fieldName, newValue]) => ({ fieldName, newValue }))
        );
        queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_SEARCH_KEY });
        return { ...newRow, ...linkedFields };
      } catch (err) {
        showSnackbar(err instanceof Error ? err.message : 'Could not update the Heartbeat link', 'error');
        return oldRow;
      }
    },
    [processRowUpdate, isGroboticAdmin, setHeartbeatLink, callerId, showSnackbar, patchOrganizationFieldsInCache, queryClient]
  );

  // Delete button click - opens confirmation popover
  const handleDeleteClick = useCallback((event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget as HTMLElement);
  }, []);

  // Track selection changes for the inline mass update hook
  const changeRowSelectionModel = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      setRowSelectionModel(newSelectionModel);
      if (newSelectionModel?.ids?.size === 1) {
        const selectedOrg = processedRows?.find(
          (o) => o?.org_uuid === Array.from(newSelectionModel?.ids)?.[0]
        ) as OrganizationTableItem | undefined;
        setSelectedOrganization(selectedOrg ?? null);
      } else {
        setSelectedOrganization(null);
      }
    },
    [processedRows]
  );

  const handleRowClick = useCallback(
    (row: OrganizationTableItem | Task) => {
      // A row with neither key can't be navigated to. The old fallback used a
      // bare org_uuid, which built a path the :PK route can't resolve, and a
      // row missing both produced `/organizations/undefined/details`.
      const fullRoute = orgDetailsRoute(row as OrganizationTableItem);
      if (!fullRoute) {
        showSnackbar('That organization is still being created. Try again in a moment.', 'info');
        return;
      }
      navigate(fullRoute);
    },
    [navigate, showSnackbar]
  );

  // Photo upload handler for single selection
  const handlePhotoUpload = useCallback(
    (file: File, selectedId: string | number) => {
      const selectedOrg = processedRows?.find(
        (o) => o?.org_uuid === selectedId || o?.PK === selectedId
      ) as OrganizationTableItem | undefined;

      if (file && selectedOrg) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('orgId', selectedOrg.org_uuid);
        formData.append('PK', selectedOrg.PK);
        formData.append('SK', selectedOrg.SK);

        uploadMutation(
          {
            PK: selectedOrg.PK,
            SK: selectedOrg.SK,
            orgId: selectedOrg.org_uuid,
            formData,
          },
          {
            onSuccess: (data: { success: boolean; photoUrl: string }) => {
              showSnackbar('Photo uploaded successfully!', 'success');
              const fieldsToUpdate = [
                { fieldName: 'photoUrl', newValue: data.photoUrl },
              ];
              syncOrganizationFields(
                queryClient,
                selectedOrg as any,
                fieldsToUpdate
              );
            },
            onError: () => {
              showSnackbar(
                'Failed to upload photo. Please try again.',
                'error'
              );
            },
          }
        );
      }
    },
    [
      processedRows,
      uploadMutation,
      showSnackbar,
      syncOrganizationFields,
      queryClient,
    ]
  );
  // Delete confirmation handler
  const handleConfirmDelete = useCallback(
    async (selectionModel: GridRowSelectionModel) => {
      const itemsToDelete = Array.from(selectionModel.ids)
        .map((id) =>
          processedRows.find((org) => org.org_uuid === id || org.PK === id)
        )
        .filter((org): org is OrganizationTableItem => !!(org?.PK && org?.SK))
        .map((org) => ({ PK: org.PK, SK: org.SK }));

      if (itemsToDelete.length === 0) {
        showSnackbar('No valid organizations selected for deletion.', 'error');
        return;
      }

      deleteItems(
        {
          EntityType: 'organization',
          items: itemsToDelete,
        },
        {
          onSuccess: () => {
            handleClosePopover();
            showSnackbar(
              `${itemsToDelete.length} organization${itemsToDelete.length > 1 ? 's' : ''} deleted successfully`,
              'success'
            );
            refresh();
          },
          onError: (error: any) => {
            showSnackbar(
              error?.message || 'Failed to delete organizations',
              'error'
            );
          },
        }
      );
    },
    [processedRows, deleteItems, showSnackbar, refresh]
  );

  const handleClosePopover = useCallback(() => {
    setAnchorEl(null);
    setRowSelectionModel({ ids: new Set<GridRowId>(), type: 'include' });
    setSelectedOrganization(null);
  }, []);

  const changeDensity = useCallback((newDensity: GridDensity) => {
    setDensity(newDensity);
    localStorage.setItem('orgDensityModel', newDensity);
  }, []);

  // Create a no-op toggleExpanded for server-side mode (no tree)
  const toggleExpanded = useCallback(() => {}, []);

  // Highlight editable cells when multiple rows are selected (mass update mode)
  // Exclude parent org rows from highlighting
  const getCellClassName = useCallback(
    (
      params: { field: string; row?: any },
      selectionModel: GridRowSelectionModel
    ) => {
      // Don't highlight parent org rows
      if (params.row?.isParentOrg) {
        return '';
      }
      const isMultiSelect = selectionModel?.ids?.size > 1;
      const isEditableField = EDITABLE_FIELDS.includes(params.field);

      if (isMultiSelect && isEditableField) {
        return 'mass-update-target';
      }
      return '';
    },
    []
  );

  // Prevent parent org rows from being selected (checkbox disabled)
  const isRowSelectable = useCallback((params: { row: any }) => {
    return !params.row?.isParentOrg && !params.row?.isProduct;
  }, []);

  // Prevent editing on parent organization rows (orgs that have child orgs)
  // Organizations with isParentOrg=true should not be editable via inline editing,
  // EXCEPT for the NetSuite/Heartbeat cross-reference fields: the point of
  // editing them is to correct parent orgs whose matcher pass wrote a child's
  // NetSuite ID into them. Those fields are admin-only, so the carve-out is too.
  const PARENT_OVERRIDE_FIELDS = new Set([
    'netsuite_id',
    'netsuite_account_name',
    'heartbeat_account_id',
  ]);
  const isCellEditable = useCallback(
    (
      params: { row: any; field: string },
      selectionModel: GridRowSelectionModel
    ) => {
      // Block editing on parent organization rows — except the NS/HB carve-out.
      if (
        params.row?.isParentOrg &&
        !PARENT_OVERRIDE_FIELDS.has(params.field)
      ) {
        return false;
      }
      if (PARENT_OVERRIDE_FIELDS.has(params.field)) {
        if (!isGroboticAdmin) return false;
        // One Heartbeat account per org: never mass-edited across a selection.
        if (
          params.field === 'heartbeat_account_id' &&
          (selectionModel?.ids?.size ?? 0) > 1
        ) {
          return false;
        }
      }
      // Check if field is in the editable fields list
      return EDITABLE_FIELDS.includes(params.field);
    },
    [isGroboticAdmin]
  );

  const columns: GridColDef[] = useMemo(
    () => organizationGridColumns(density, handleRowClick, toggleExpanded),
    [density, handleRowClick, toggleExpanded, organizationGridColumns]
  );

  const defaultColumnWidths = useMemo(
    () =>
      Object.fromEntries(
        columns
          .filter((col) => typeof col.width === 'number')
          .map((col) => [col.field, col.width])
      ),
    [columns]
  );

  // Delete confirmation popover
  const deletePopover = useMemo(
    () => (
      <OrganizationsPopover
        id={anchorEl ? 'org-delete-popover' : undefined}
        open={Boolean(anchorEl)}
        onClose={handleClosePopover}
        handleConfirmDelete={() => handleConfirmDelete(rowSelectionModel)}
        selectedData={selectedOrganization}
        anchorEl={anchorEl}
      />
    ),
    [
      anchorEl,
      handleClosePopover,
      handleConfirmDelete,
      rowSelectionModel,
      selectedOrganization,
    ]
  );

  // Footer props for SelectionFooter
  const footerProps = useMemo(
    () => ({
      rowSelectionModel,
      setRowSelectionModel,
      apiRef,
      onDeleteClick: handleDeleteClick,
      onPhotoUpload: handlePhotoUpload,
      showPhotoUpload: true,
      itemLabel: 'organizations',
      serverSideMode: true,
      totalRowCount,
      DeleteConfirmationPopover: deletePopover,
    }),
    [
      rowSelectionModel,
      setRowSelectionModel,
      apiRef,
      handleDeleteClick,
      handlePhotoUpload,
      totalRowCount,
      deletePopover,
    ]
  );
  // Show loading state while checking search availability or waiting for index to initialize
  // if (searchAvailable === null || searchAvailable === false) {
  //   return (
  //     <Box
  //       sx={{
  //         display: 'flex',
  //         flexDirection: 'column',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '100vh',
  //         gap: 2,
  //       }}
  //     >
  //       <CircularProgress />
  //       <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
  //         {searchAvailable === null ? 'Connecting to search service...' : 'Initializing search index...'}
  //       </Box>
  //     </Box>
  //   )
  // }

  // Show error if search failed
  if (searchError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Search error: {searchError}
        </Alert>
        <Button variant="outlined" onClick={() => refresh()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={processedRows}
        apiRef={apiRef}
        context={'organizations'}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        aggregationModel={{
          total: 'sum',
          monthlyTotal: 'sum',
          spend: 'sum',
          grossProfit: 'sum',
          totalGrossProfit: 'sum',
          implementationFee: 'sum',
        }}
        pinnedColumns={pinnedColumns}
        onRowSelectionModelChange={changeRowSelectionModel}
        onChangeDensity={changeDensity}
        getRowClassName={getOrgRowClassNames}
        getCellClassName={getCellClassName}
        isCellEditable={isCellEditable}
        isRowSelectable={isRowSelectable}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={SelectionFooter}
        footerProps={footerProps}
        // Inline mass updates - editing a cell updates all selected rows
        onProcessRowUpdate={handleProcessRowUpdate}
        allowInlineMassEdit={true} // Allow editing cells when multiple rows selected
        treeData={false} // Disable tree data in server-side mode
        // gridSxProps={gridSxProps}
        disableChildFilters={false}
        isLoading={isLoading}
        // Server-side mode props
        serverSideMode={true}
        rowCount={totalRowCount}
        paginationModel={serverSideProps.paginationModel}
        onPaginationModelChange={serverSideProps.onPaginationModelChange}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onClearFilters={onClearFilters}
        syncStatus={syncStatus}
        // Pass server-calculated aggregations (not affected by client-side sorting/pagination)
        serverAggregations={aggregations}
      />
    </>
  );
};

export default OrganizationsPageServerSide;
